import { createSelector } from 'reselect';

import {
    filterTrips,
    tripSatisfiesFilters,
    filterNearby,
    filterTripsWithNearby,
    searchResultsSelectors,
    sortTrips,
} from 'wanderu-ducks-psearch';
import { tripSelectors } from 'wanderu-ducks-trips';
import {
    selectCurrency,
    selectLocale,
} from 'wanderu-i18n';
import { getAllConversionRates } from 'wanderu-service-currency';

import {
    selectFilterId,
    selectFilterData,
    selectActiveSortType,
} from '../../filterData/selectors';
import {
    selectPSearchParams,
    selectLeavesAfter,
} from '../../search/selectors';

function selectSearchResults(state, searchParams) {
    const psearchParams = selectPSearchParams(state, searchParams);
    return searchResultsSelectors.selectSearchResults(state, psearchParams);
}

function selectActiveSortTypeFromFilterId(state, searchParams) {
    const filterId = selectFilterId(state, searchParams);
    return selectActiveSortType(state, filterId);
}

function selectTripScores(state, searchParams) {
    const psearchParams = selectPSearchParams(state, searchParams);
    return searchResultsSelectors.selectTripScores(state, psearchParams);
}

const selectSortedTrips = createSelector(
    selectSearchResults,
    selectTripScores,
    selectActiveSortTypeFromFilterId,
    (searchResults, tripScores, activeSortType) => {
        return sortTrips(searchResults, tripScores, activeSortType);
    },
);

const selectFilteredTripsWithNearby = createSelector(
    selectFilterData,
    selectLeavesAfter,
    selectSearchResults,
    filterTripsWithNearby,
);

const selectFilteredTrips = createSelector(
    selectFilterData,
    selectFilteredTripsWithNearby,
    selectTripMetadata,
    filterNearby,
);

const selectFilteredAndSortedTrips = createSelector(
    selectFilterData,
    selectLeavesAfter,
    selectSortedTrips,
    selectTripMetadata,
    (
        filterData,
        leavesAfter,
        trips,
        tripMetadata,
    ) => {
        return filterTrips(filterData, leavesAfter, trips, tripMetadata);
    },
);

function selectTripSatisfiesFilters(state, tripId, searchParams) {
    const filterData = selectFilterData(state, searchParams);
    const leavesAfter = selectLeavesAfter(state, searchParams);
    const tripMetadata = selectTripMetadata(state, searchParams);
    const metadata = tripMetadata[tripId];
    const trip = tripSelectors.selectTrip(state, tripId);
    return tripSatisfiesFilters(filterData, leavesAfter, trip, metadata);
}

function selectTripMetadata(state, searchParams) {
    const psearchParams = selectPSearchParams(state, searchParams);
    return searchResultsSelectors.selectTripMetadata(state, psearchParams);
}

function selectLiveCheckContext(appState, tripIds) {
    const exchangeRates = getAllConversionRates();
    const currency = selectCurrency(appState);
    const locale = selectLocale(appState);
    const exchangeRate = exchangeRates[currency];

    const tripCtx = tripIds.reduce((obj, tripId) => {
        const trip = tripSelectors.selectTripData(appState, tripId);
        const tripCurrency = Object.keys(trip.pricing)[0];

        return {
            ...obj,
            [tripId]: {
                exchangeRate: exchangeRate[tripCurrency],
                lastScraped: trip.created,
                originalPricing: trip.pricing,
                // Seat count the original price represents — the livecheck
                // status normalizes by this (not the requested pax count) so a
                // per-person single-pax-cache price isn't double-discounted.
                originalSeats: trip.seats,
                selectedFare: trip.selectedFare,
            },
        };
    }, {});

    return {
        currency,
        locale,
        tripLiveCheckData: tripCtx,
    };
}

export {
    selectFilteredAndSortedTrips,
    selectFilteredTrips,
    selectFilteredTripsWithNearby,
    selectLiveCheckContext,
    selectSearchResults,
    selectTripMetadata,
    selectTripSatisfiesFilters,
};
